import axios from 'axios';
import Link from 'next/link';
import Router from 'next/router';
import range from 'lodash.range';
import { HrBrick } from '../HrBrick.jsx';
import * as gtag from '../../utils/gtag.js';
import { apiUrl } from '../../utils/api.js';
import { LoanInput } from './LoanInput.jsx';
import { MoneyInput } from '../MoneyInput.jsx';
import { ButtonLink } from '../ButtonLink.jsx';
import { SelectInput } from '../SelectInput.jsx';
import { SubmitButton } from '../SubmitButton.jsx';
import { useState, useEffect, useRef, Fragment } from 'react';
import { formatNumber } from '../../utils/format-number.js';
import { useStickyState } from '../../hooks/sticky-state.js';
import { RepeatableFormFields } from '../RepeatableFormFields/';
import { BoliglanIllustrationPartial } from '../BoliglanIllustrationPartial.jsx';

function TheForm({
  onSubmit,
  isHidden,
  view,
  firstLoanType = null,
  firstInterestRate = null,
}) {
  const [canSubmit, setCanSubmit] = useState(false);
  const [yearlyIncome, setYearlyIncome] = useState(null);
  const [age, setAge] = useState(null);
  const [loans, setLoans] = useState([
    { type: firstLoanType, interestRate: firstInterestRate },
  ]);
  const [recentlyCreatedEntry] = useStickyState(
    'recently-created-personal-loan-entry',
    null
  );

  const submitButtonElement = useRef();

  useEffect(
    function syncCanSubmit() {
      const value = Boolean(loans.length && yearlyIncome && age);

      if (canSubmit !== value) {
        setCanSubmit(value);
      }
    },
    [loans, yearlyIncome, age]
  );

  function handleSubmit(event) {
    event.preventDefault();

    onSubmit({
      yearlyIncome,
      age,
      loans,
    });

    window.scrollTo(0, 0);

    gtag.event({
      category: 'Forbrukslånskjema',
      action: 'Klikk på "Gå videre"',
    });
  }

  return (
    <>
      <div className="form__headline">
        {view === 'standalone' ? (
          <h1>Samle gjeld og spar penger</h1>
        ) : (
          <h2>Samle gjeld og spar penger</h2>
        )}
        <HrBrick />
      </div>
      <form
        className={`form ${isHidden ? 'form--is-hidden' : ''}`}
        onSubmit={handleSubmit}
      >
        <div className="form__fields">
          {recentlyCreatedEntry && (
            <p className="recentlyCreatedEntryNotice">
              Psst! Du har allerede en forbrukslåns&shy;rapport hos oss:
              <span>
                <ButtonLink
                  href="/forbrukslansrapport/[id]"
                  as={`/forbrukslansrapport/${recentlyCreatedEntry}`}
                >
                  Gå til rapport
                </ButtonLink>
              </span>
            </p>
          )}
          <RepeatableFormFields
            items={loans}
            onAddItem={() =>
              setLoans(loans => [
                ...loans,
                { interestRate: null, loanAmount: null },
              ])
            }
            onRemoveItem={key =>
              setLoans(loans => loans.filter((_, i) => key !== i))
            }
          >
            {(value, key) => (
              <LoanInput
                inputKey={key}
                value={value}
                onChange={valueResolver =>
                  setLoans(loans => {
                    const newLoans = [...loans];

                    newLoans[key] = valueResolver(loans[key]);

                    return newLoans;
                  })
                }
                defaultType={key === 0 && firstLoanType}
                defaultInterestRate={(key === 0 && firstInterestRate) || null}
              />
            )}
          </RepeatableFormFields>
          <MoneyInput
            label="Hva er årsinntekten din? (før skatt)"
            onChange={setYearlyIncome}
            required
          />
          <SelectInput
            label="Hvor gammel er du?"
            placeholder="Velg"
            options={range(18, 90).map(years => ({
              value: years,
              label: `${years} år`,
            }))}
            onChange={age => setAge(Number(age))}
            required
          />
          <div className="submitContainer">
            <SubmitButton
              type="submit"
              disabled={!canSubmit}
              ref2={submitButtonElement}
            >
              Gå videre
            </SubmitButton>
          </div>
        </div>
        <div className="illustration">
          <BoliglanIllustrationPartial />
        </div>
      </form>
      <style jsx>{`
        .form__headline h1,
        .form__headline h2 {
          font-weight: 800;
          font-size: 56px;
          color: var(--bgBlueish__text);
          margin: 32px 0 9px 0;
          line-height: 0.9;
        }

        .form__headline h2 {
          font-size: 38px;
          margin-top: 0;
        }

        .form {
          margin-top: 36px;
        }

        .form--is-hidden {
          position: absolute;
          visibility: hidden;
        }

        .form__label {
          margin-bottom: 2px;
          font-weight: 600;
          color: var(--bgBlueish__text);
        }

        .submitContainer {
          text-align: right;
        }

        .illustration {
          display: none;
        }

        .recentlyCreatedEntryNotice {
          margin: -18px 0 18px 0;
          text-align: right;
          font-weight: 500;
          color: var(--bgBlueish__text);
        }

        .recentlyCreatedEntryNotice :global(a) {
          margin-top: 9px;
        }

        .recentlyCreatedEntryNotice > span {
          display: block;
        }

        @media screen and (min-width: 750px) {
          .form__headline h1 {
            max-width: 420px;
            margin: ${view === 'inline' ? '0' : '54px'} 0 27px 0;
          }

          .form__fields {
            min-width: 420px;
            width: 420px;
          }
        }

        @media screen and (min-width: 980px) {
          .illustration {
            position: relative;
            display: block;
            margin-top: -180px;
            margin-left: 80px;
          }

          .illustration::after {
            content: '';
            position: absolute;
            top: 200px;
            right: 0;
            display: block;
            width: 480px;
            height: 480px;
            border-radius: 50%;
            background-color: #f3f5ff;
            z-index: -1;
          }

          .illustration :global(svg) {
            max-width: 100%;
            height: auto;
          }

          .form {
            display: flex;
            justify-content: space-between;
          }
        }
      `}</style>
    </>
  );
}

function SummaryScreen({
  yearlyIncome,
  age,
  loans,
  onGoingBack,
  onSubmit,
  isSubmitting,
  submissionError,
}) {
  const hasMultipleLoans = loans.length > 1;

  function handleSubmit() {
    onSubmit({
      yearlyIncome,
      age,
      loans,
    });

    gtag.event({
      category: 'Forbrukslånskjema',
      action: 'Klikk på send inn-knappen',
    });
  }

  return (
    <div className="root">
      <h1>Stemmer tallene?</h1>
      <dl className="list">
        {loans.map(({ type, interestRate, loanAmount }, key) => (
          <Fragment key={key}>
            <div className="list__line">
              <dt>Type lån:</dt>
              <dd>
                {type === 'credit_card'
                  ? 'Kredittkort'
                  : type === 'consumer'
                  ? 'Forbrukslån'
                  : type}
              </dd>
            </div>
            <div className="list__line">
              <dt>Lånebeløp:</dt>
              <dd>{formatNumber(loanAmount, null, 'kr')}</dd>
            </div>
            <div className="list__line">
              <dt>Effektiv rente:</dt>
              <dd>{(interestRate / 100).toFixed(2).replace('.', ',')} %</dd>
            </div>
            <div className="list__spacer" />
          </Fragment>
        ))}
        {hasMultipleLoans && (
          <>
            <div className="list__line">
              <dt>Lån som skal samles:</dt>
              <dd>{loans.length} lån</dd>
            </div>
            <div className="list__line">
              <dt>Totalt lånebeløp:</dt>
              <dd>
                {formatNumber(
                  loans.reduce((sum, loan) => sum + loan.loanAmount, 0),
                  null,
                  'kr'
                )}
              </dd>
            </div>
            <div className="list__spacer" />
          </>
        )}
        <div className="list__line">
          <dt>Årsinntekt:</dt>
          <dd>{formatNumber(yearlyIncome, null, 'kr')}</dd>
        </div>
        <div className="list__line">
          <dt>Alder:</dt>
          <dd>{age} år</dd>
        </div>
      </dl>
      {submissionError && (
        <div className="submissionError">{submissionError.message}</div>
      )}
      <div className="buttons">
        {!isSubmitting && (
          <SubmitButton type="button" level="secondary" onClick={onGoingBack}>
            Gå tilbake
          </SubmitButton>
        )}
        <SubmitButton
          type="button"
          disabled={isSubmitting}
          onClick={handleSubmit}
          isLoading={isSubmitting}
          loadingLabel="Henter din rapport..."
        >
          Ja! Vis sparemuligheter
        </SubmitButton>
      </div>
      <div className="privacyText">
        <p>
          Når du går videre, godtar du at vi lagrer og bruker opplysningene du
          har oppgitt til å danne et bedre sammenlignings&shy;grunnlag i
          tjenesten.
        </p>
        <p>
          Det er derfor viktig at du passer på å fylle inn så nøyaktige
          opplysninger som mulig, da dette vil gi deg et mer nøyaktig estimat på
          hvor mye du vil spare.
        </p>
        <p>
          Les mer om databehandling i{' '}
          <Link href="/personvern">
            <a target="_blank">vår personvernerklæring</a>
          </Link>
          .
        </p>
      </div>
      <style jsx>{`
        .root {
          border-radius: 2px;
          background-color: var(--bgBlueish);
          color: var(--bgBlueish__text);
        }

        .list__line {
          display: flex;
          justify-content: space-between;
          margin: 14px 0;
          padding-bottom: 4px;
          border-bottom: 2px dashed rgba(0, 0, 0, 0.06);
        }

        .list__spacer {
          margin: 28px 0;
        }

        .list {
          font-size: 16px;
          color: var(--bgBlueish__text);
        }

        .list dt {
          font-weight: 700;
        }

        .list dd {
          font-weight: 500;
        }

        .buttons {
          margin: 36px 0;
          display: flex;
          justify-content: flex-end;
        }

        .buttons :global(button:last-child) {
          margin-left: 18px;
        }

        .privacyText {
          margin-top: 36px;
          font-size: 14px;
          color: var(--bgBlueish__textDim);
          font-weight: 500;
          line-height: 1.5;
        }

        .privacyText a {
          color: currentColor;
        }

        .privacyText a:hover {
          color: var(--bgBlueish__text);
        }

        .submissionError {
          margin-top: 18px;
          margin-bottom: -18px;
          color: #fb1919;
          font-weight: 500;
          line-height: 1.5;
        }

        .submissionError p {
          margin: 0;
        }

        .submissionError p + p {
          margin-top: 18px;
        }

        @media screen and (min-width: 750px) {
          .root {
            max-width: 420px;
          }
        }
      `}</style>
    </div>
  );
}

function sleep(ms) {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

function getUserOrigin() {
  if (!window.localStorage) {
    return null;
  }

  const payload = window.localStorage.getItem('pengerno-user-origin');

  return payload ? JSON.parse(payload) : null;
}

export function PersonalLoanReportForm({
  view = 'standalone',
  firstLoanType = null,
  firstInterestRate = null,
}) {
  const [data, setData] = useState(null);
  const [isSummaryScreenVisible, setIsSummaryScreenVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [recentlyCreatedEntry, setRecentlyCreatedEntry] = useStickyState(
    'recently-created-personal-loan-entry',
    null
  );

  useEffect(() => {
    setSubmissionError(null);
  }, [isSummaryScreenVisible]);

  async function handleSubmit(data) {
    setIsSubmitting(true);

    try {
      const responsePromise = axios.post(apiUrl('personal-loan-entries'), {
        ...data,
        recentlyCreatedEntry,
        userOrigin: getUserOrigin(),
      });

      const [response] = await Promise.all([responsePromise, sleep(2000)]);

      setRecentlyCreatedEntry(response.data.id);

      Router.push('/forbrukslansrapport/[id]', response.data.reportUrl).then(
        () => {
          window.scrollTo(0, 0);
        }
      );
    } catch (error) {
      if (!error.isAxiosError) {
        throw error;
      }

      gtag.event({
        category: 'Forbrukslånskjema',
        action: 'Innsendingsfeil',
        label: JSON.stringify(error.response.data),
      });

      await sleep(3000);

      setIsSubmitting(false);

      setSubmissionError({
        message: (
          <>
            <p>Det er en feil som gjør at opplysningene ikke kan behandles.</p>
            <p>
              Mest sannsynlig er det en eller flere verdier som ikke er gyldige.
              Prøv igjen eller gå tilbake for å endre på opplysningene.
            </p>
          </>
        ),
      });
    }
  }

  return (
    <>
      <TheForm
        onSubmit={data => {
          setData(data);

          setIsSummaryScreenVisible(true);
        }}
        isHidden={isSummaryScreenVisible}
        view={view}
        firstLoanType={firstLoanType}
        firstInterestRate={firstInterestRate}
      />
      {isSummaryScreenVisible && (
        <SummaryScreen
          isSubmitting={isSubmitting}
          submissionError={submissionError}
          onGoingBack={() => {
            setIsSummaryScreenVisible(false);

            gtag.event({
              category: 'Forbrukslånskjema',
              action: 'Klikk på "Gå tilbake"',
            });
          }}
          onSubmit={handleSubmit}
          {...data}
        />
      )}
    </>
  );
}
